/**
 * `StorageItem` → the flat row the browser grid renders and copies. Column
 * ids are fixed per mode (spec §6.3: no column selector); `null` cells copy as
 * empty text through the shared `buildCopyText`.
 */
import { formatStorageDateTime } from './storageDateTime';
import { storageResourceKey, type StorageItem } from '../../../../types/azureStorage';
import type { StorageExplorerMode } from '../../core/types/workspaceTypes';
import { isNumericColumn } from '../../common/utils/columnAlignment';
import { lastPathSegment, storageItemRef } from './storagePaths';

export type StorageBrowserColumnId = 'name' | 'size' | 'lastModified' | 'tier' | 'type' | 'status' | 'owner' | 'group' | 'permissions';

/** `dataType` feeds the shared `isNumericColumn` (alignment decided from metadata, never from values). */
export type StorageBrowserColumn = { id: StorageBrowserColumnId; label: string; dataType: string; numeric: boolean; width: number };

function column(id: StorageBrowserColumnId, label: string, dataType: string, width: number): StorageBrowserColumn {
  return { id, label, dataType, numeric: isNumericColumn({ dataType }), width };
}

const NAME = column('name', 'Name', 'nvarchar', 220); // the minimum: Name fills the rest of the width (U10)
const SIZE = column('size', 'Size', 'bigint', 90); // C1 (N12): "792 KB" needs < 90 px; the width went to Last modified
// C1 (N12): 180 px cut the time off "12/31/2026, 10:10:43 PM" on most listings; the exact UTC instant is the cell's tooltip.
const LAST_MODIFIED = column('lastModified', 'Last modified', 'datetime2', 205);
const TIER = column('tier', 'Tier', 'nvarchar', 90);
const TYPE = column('type', 'Type', 'nvarchar', 110);
const STATUS = column('status', 'Status', 'nvarchar', 120);
// S8: the Data Lake view's POSIX columns (from the listing's `hns` facet).
const OWNER = column('owner', 'Owner', 'nvarchar', 130);
const GROUP = column('group', 'Group', 'nvarchar', 130);
const PERMISSIONS = column('permissions', 'Permissions', 'nvarchar', 110);

/** The Name column's minimum width; it takes the rest of the grid's width (UI/UX fix round A, U10). */
export const STORAGE_NAME_MIN_WIDTH = NAME.width;

/**
 * Fixed column set per mode (spec §6.3). UI/UX fix round A (U10): the grid passes `hasStatus: false` while no loaded row
 * has a status, and the Status column stays hidden until one does (copy / export keep the full set).
 */
export function storageBrowserColumnsFor(mode: StorageExplorerMode, options: { hasStatus?: boolean } = {}): StorageBrowserColumn[] {
  const columns = fixedColumnsFor(mode);
  return options.hasStatus === false ? columns.filter((candidate) => candidate.id !== 'status') : columns;
}

function fixedColumnsFor(mode: StorageExplorerMode): StorageBrowserColumn[] {
  switch (mode) {
    case 'blobs':
      return [NAME, SIZE, LAST_MODIFIED, TIER, TYPE, STATUS];
    case 'files':
      return [NAME, SIZE, LAST_MODIFIED, TYPE];
    case 'hns':
      return [NAME, SIZE, LAST_MODIFIED, TYPE, OWNER, GROUP, PERMISSIONS];
    case 'entities':
    case 'messages':
      return [NAME, TYPE];
  }
}

export type StorageBrowserRow = {
  /** `storageResourceKey` of the item's ref — the selection / navigation identity. */
  key: string;
  item: StorageItem;
  isFolder: boolean;
  name: string;
  size: number | null;
  lastModified: string | null;
  tier: string | null;
  type: string;
  status: string;
  pending?: boolean;
  /** S8: Data Lake rows only (the `hns` facet); `null` elsewhere. */
  owner?: string | null;
  group?: string | null;
  permissions?: string | null;
};

export function pendingStorageFolderToRow(folder: { container: string; prefix: string }): StorageBrowserRow {
  const item: StorageItem = { kind: 'folder', container: folder.container, prefix: folder.prefix };
  return { ...storageItemToRow(item), pending: true, status: 'empty — upload something into it' };
}

function blobStatus(item: Extract<StorageItem, { kind: 'blob' }>): string {
  const parts: string[] = [];
  if (item.deleted) parts.push('deleted');
  if (item.leaseState && item.leaseState !== 'available') parts.push(item.leaseState);
  if (item.archiveStatus) parts.push(item.archiveStatus);
  return parts.join(' · ');
}

/**
 * Readable type labels (UI/UX fix round A, reviews U10 / A19): "Folder" for a virtual folder and a directory alike,
 * "Block blob" rather than the service's `BlockBlob`, and "File" for a Data Lake file (the DFS listing carries no blob
 * type). The service names stay in the Properties panel.
 */
const BLOB_TYPE_LABELS: Record<string, string> = { BlockBlob: 'Block blob', AppendBlob: 'Append blob', PageBlob: 'Page blob' };

/** The readable blob type (the grid and, since C1, the properties panel): "Block blob" for `BlockBlob`. */
export const blobTypeLabel = (blobType: string): string => BLOB_TYPE_LABELS[blobType] ?? blobType;

function blobType(item: Extract<StorageItem, { kind: 'blob' }>): string {
  if (item.snapshot) return 'Snapshot';
  if (item.versionId && item.isCurrentVersion === false) return 'Version';
  if (item.hns && !item.blobType) return 'File';
  return item.blobType ? blobTypeLabel(item.blobType) : 'Blob';
}

/** `fullPath` (UI review F3): a Flat listing names each blob / folder by its full path in the container — the leaf alone
 *  would make same-named blobs in different folders look identical. Hierarchy keeps the leaf (the breadcrumb says where). */
export type StorageRowOptions = { fullPath?: boolean };

export function storageItemToRow(item: StorageItem, options: StorageRowOptions = {}): StorageBrowserRow {
  const row = storageItemToBaseRow(item, options.fullPath === true);
  const facts = item.kind === 'folder' || item.kind === 'blob' ? item.hns : undefined;
  return facts ? { ...row, owner: facts.owner ?? null, group: facts.group ?? null, permissions: facts.permissions ?? null } : row;
}

function storageItemToBaseRow(item: StorageItem, fullPath: boolean): StorageBrowserRow {
  const ref = storageItemRef(item);
  const key = ref ? storageResourceKey(ref) : JSON.stringify(item);
  const pathName = (path: string) => (fullPath ? path.replace(/\/$/u, '') : lastPathSegment(path));
  switch (item.kind) {
    case 'folder':
      return { key, item, isFolder: true, name: pathName(item.prefix), size: null, lastModified: null, tier: null, type: 'Folder', status: '' };
    case 'blob':
      return {
        key,
        item,
        isFolder: false,
        name: pathName(item.name),
        size: item.size,
        lastModified: item.lastModified ?? null,
        tier: item.tier ?? null,
        type: blobType(item),
        status: blobStatus(item),
      };
    case 'container':
      return { key, item, isFolder: true, name: item.name, size: null, lastModified: item.lastModified ?? null, tier: null, type: 'Container', status: item.deleted ? 'deleted' : '' };
    case 'directory':
      return { key, item, isFolder: true, name: lastPathSegment(item.path), size: null, lastModified: null, tier: null, type: 'Folder', status: '' };
    case 'file':
      return { key, item, isFolder: false, name: lastPathSegment(item.path), size: item.size, lastModified: item.lastModified ?? null, tier: null, type: 'File', status: '' };
    case 'table':
      return { key, item, isFolder: false, name: item.name, size: null, lastModified: null, tier: null, type: 'Table', status: '' };
    case 'queue':
      return { key, item, isFolder: false, name: item.name, size: null, lastModified: null, tier: null, type: 'Queue', status: '' };
    case 'share':
      return { key, item, isFolder: true, name: item.name, size: null, lastModified: null, tier: null, type: 'Share', status: '' };
  }
}

/** The copy / export record shape (column id → display-agnostic value). */
export function storageRowToRecord(row: StorageBrowserRow): Record<string, unknown> {
  return { name: row.name, size: row.size, lastModified: row.lastModified, tier: row.tier, type: row.type, status: row.status, owner: row.owner ?? null, group: row.group ?? null, permissions: row.permissions ?? null };
}

export function formatStorageSize(bytes: number | null): string {
  if (bytes === null) return '';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

/** The grid's timestamp — the ONE storage date-time format (`storageDateTime.ts`, U11). */
export function formatStorageTimestamp(iso: string | null): string {
  return formatStorageDateTime(iso);
}
